/*
Facet bar for the example gallery: a search box, one dropdown per facet with
live counts, and a row of removable chips for what's currently applied.
*/
import React, { useState } from "react";
import PropTypes from "prop-types";

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import { FACETS, facetOptions, countSelected } from "./exampleFilters";

function FacetMenu({ facet, options, selected, onToggle }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  return (
    <>
      <Badge
        badgeContent={count}
        color="primary"
        overlap="rectangular"
        sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", height: 16, minWidth: 16 } }}
      >
        <Button
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={<ExpandMoreIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.8125rem",
            borderRadius: "8px",
            px: 1.5,
            border: "1px solid",
            borderColor: count ? "primary.main" : "divider",
            color: count ? "primary.main" : "text.secondary",
            backgroundColor: count ? "primary.extraLight" : "transparent",
            "&:hover": { borderColor: "primary.main", backgroundColor: "primary.extraLight" },
          }}
        >
          {facet.label}
        </Button>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxHeight: 360, width: 260, borderRadius: 2 } } }}
      >
        {options.length === 0 && (
          <MenuItem disabled>
            <Typography variant="caption">Nothing to filter by yet</Typography>
          </MenuItem>
        )}
        {options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          // Never disable something already ticked, or it can't be un-ticked.
          const disabled = opt.count === 0 && !isSelected;
          return (
            <MenuItem
              key={opt.value}
              onClick={() => onToggle(facet.key, opt.value)}
              disabled={disabled}
              dense
            >
              <Checkbox checked={isSelected} size="small" sx={{ p: 0.5, mr: 1 }} />
              <ListItemText
                primary={opt.label}
                slotProps={{ primary: { fontSize: "0.8125rem", fontWeight: isSelected ? 700 : 500 } }}
              />
              <Typography variant="caption" sx={{ color: "text.disabled", ml: 1, fontVariantNumeric: "tabular-nums" }}>
                {opt.count}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

FacetMenu.propTypes = {
  facet: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default function ExampleFilterBar({
  examples,
  selections,
  query,
  onToggle,
  onQueryChange,
  onClearAll,
  resultCount,
}) {
  const activeCount = countSelected(selections);
  const hasFilters = activeCount > 0 || Boolean(query);

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search by name, industry, or place"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => onQueryChange("")}
                  sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{ mb: 1.25, "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } }}
      />

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, justifyContent: "center", alignItems: "center" }}>
        {FACETS.map((facet) => (
          <FacetMenu
            key={facet.key}
            facet={facet}
            options={facetOptions(examples, selections, query, facet)}
            selected={selections[facet.key] || []}
            onToggle={onToggle}
          />
        ))}
        {hasFilters && (
          <Button
            size="small"
            onClick={onClearAll}
            sx={{ textTransform: "none", fontSize: "0.75rem", color: "text.secondary" }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      {/* Result count doubles as the live region, so filtering is announced. */}
      <Typography
        aria-live="polite"
        variant="caption"
        sx={{ display: "block", textAlign: "center", mt: 1, color: "text.secondary" }}
      >
        {hasFilters
          ? `Showing ${resultCount} of ${examples.length} examples`
          : `${examples.length} examples`}
      </Typography>

      {activeCount > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, justifyContent: "center", mt: 1 }}>
          {FACETS.flatMap((facet) =>
            (selections[facet.key] || []).map((value) => (
              <Chip
                key={`${facet.key}:${value}`}
                size="small"
                label={`${facet.label}: ${facet.format(value)}`}
                onDelete={() => onToggle(facet.key, value)}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "primary.main",
                  backgroundColor: "primary.extraLight",
                  "& .MuiChip-deleteIcon": { fontSize: 14 },
                }}
              />
            ))
          )}
        </Stack>
      )}
    </Box>
  );
}

ExampleFilterBar.propTypes = {
  examples: PropTypes.array.isRequired,
  selections: PropTypes.object.isRequired,
  query: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
};
